import { immerable } from "immer";

import { RangeModifier } from "../../connections/models/RangeModifier";
import { RangeModifierOperations, type ScheduleData } from "../../connections/models/RangeModifierOperations";
import { type GroupConnection } from "../../connections/models/GroupConnection";
import { emptyCompoundId } from "../../connections/models/emptyCompoundId";
import {
    type GroupLensConnection,
    type InputVendorDataItem,
    type CameraKitLens as Lens,
    type VendorDataItem,
} from "../../../gql-generated/types";
import { type SaveLensMutationVariables } from "../../lenses/api/mutation";
import { DateConvert } from "../../lenses/domain/services/DateConvert";
import { LensLabels, type LensListItemModel } from "../../lenses/models";
export class LensInGroupModel {
    [immerable] = true;
    readonly lens: Readonly<Partial<Lens>> = { vendorData: [], compoundId: emptyCompoundId };
    readonly priority: number = 0;
    readonly isNew: boolean = false;
    readonly isValid: boolean = false;
    readonly scheduleRange: RangeModifier = new RangeModifier();
    readonly vendorData: readonly VendorDataItem[] = [];
    readonly isUpdated: boolean = false;
    readonly isRemoved: boolean = false;
    readonly updatedAt: Date | null = null;
    readonly scheduleV3Alias: string | null = null;

    get isNull() {
        return this.id === null;
    }

    get id() {
        return this.lens?.compoundId?.id;
    }

    static createFromLens(lens: LensListItemModel, priority: number): LensInGroupModel {
        return LensInGroupModel.createDefault({
            lens: {
                ...lens,
                updatedAt: DateConvert.toTimestamp(lens.updatedAt),
                removed: false,
            } as Partial<Lens>,
            priority,
            isNew: true,
            isValid: true,
            scheduleV3Alias: lens.scheduleV3Alias,
            updatedAt: lens.updatedAt,
        });
    }

    static createFromConnection({ lens, priority, vendorData, schedule }: GroupLensConnection) {
        return LensInGroupModel.createDefault({
            lens: lens ?? {},
            priority: priority ?? 0,
            isNew: false,
            isValid: true,
            vendorData: vendorData?.map((data) => ({ key: data?.key ?? "", value: data?.value ?? "" })),
            updatedAt: DateConvert.toDate(lens?.updatedAt),
            scheduleRange: RangeModifierOperations.fromSchedule(schedule as ScheduleData),
            scheduleV3Alias: LensLabels.getScheduleV3Alias(lens?.labels ?? []),
        });
    }

    private static createDefault(data: Partial<LensInGroupModel> | null = null): LensInGroupModel {
        return Object.assign(new LensInGroupModel(), data);
    }

    getMutationVariables(vendorData: readonly InputVendorDataItem[]): SaveLensMutationVariables {
        return {
            lensCompoundId: this.lens.compoundId!,
            vendorData,
        };
    }

    toGroupConnection(): GroupConnection {
        return {
            priority: this.priority,
            scheduleRange: this.scheduleRange,
            vendorData: this.vendorData,
            lensCompoundId: this.lens.compoundId ?? emptyCompoundId,
        };
    }
}
